import React from 'react';
import { Admin, Resource } from 'react-admin';

import * as AccessCheck from '../Common/Utils/AccessCheck';
import * as AdSize from '../Components/AdSizes';
import * as CampaignAdQuality from '../Components/Campaigns/AdQuality';
import * as CampaignAdTag from '../Components/Campaigns/AdTag';
import * as CampaignPmp from '../Components/Campaigns/Pmp';
import * as CampaignRtb from '../Components/Campaigns/Rtb';
import * as CampaignSite from '../Components/Campaigns/Site';
import * as CreativeTemplate from '../Components/CreativeTemplates';
import * as Documentation from '../Components/Documentations';
import * as JsFunction from '../Components/JsFunctions';
import * as MacroMap from '../Components/MacroMaps';
import * as MacroMapKey from '../Components/Managed/MacroMapKeys';
import * as Monitor from '../Components/Monitors';
import * as Network from '../Components/Networks';
import * as Placement from '../Components/Placements';
import * as Profile from '../Components/Profiles';
import * as Property from '../Components/Managed/Properties';
import * as PropertyGroup from '../Components/Managed/PropertyGroups';
import * as ReportChangeHistory from '../Components/Reports/ChangeHistory';
import * as ReportLinkedPlacement from '../Components/Reports/LinkedPlacements';
import * as ReportPlacement from '../Components/Reports/Placements';
import * as ReportSite from '../Components/Reports/Sites';
import * as ServerFile from '../Components/ServerFiles';
import * as Team from '../Components/Teams';
import * as User from '../Components/Users';

import BarChartIcon from '@material-ui/icons/BarChart';
import CreateIcon from '@material-ui/icons/Create';
import NoteIcon from '@material-ui/icons/Notes';
import PeopleIcon from '@material-ui/icons/People';
import PersonIcon from '@material-ui/icons/Person';
import RadioButtonIcon from '@material-ui/icons/RadioButtonChecked';
import StorageIcon from '@material-ui/icons/Storage';

import AuthProvider from './AuthProvider';
import Dashboard from './Dashboard';
import Layout from './Layout';
import LeftMenu from './LeftMenu';
import LoginPage from './LoginPage';
import RestClient from './RestClient';
import Title from './Title';

const App = () => {
  return (
    <div>
      <Admin appLayout={Layout} authProvider={AuthProvider} dashboard={Dashboard} dataProvider={RestClient} loginPage={LoginPage} menu={LeftMenu} title={Title}>
        {permissions => [
          <Resource name="networks"
            list={AccessCheck.Read(permissions, Network.RequiredRole, Network.List)}
            show={AccessCheck.Read(permissions, Network.RequiredRole, Network.Show)}
            edit={AccessCheck.Read(permissions, Network.RequiredRole, Network.Edit)}
            create={AccessCheck.Create(permissions, Network.RequiredRole, Network.Create)}
            options={{ group: 'Inventory', label: 'Networks', leftIcon: <StorageIcon /> }} />,
          <Resource name="placements"
            list={AccessCheck.Read(permissions, Placement.RequiredRole, Placement.List)}
            show={AccessCheck.Read(permissions, Placement.RequiredRole, Placement.Show)}
            edit={AccessCheck.Read(permissions, Placement.RequiredRole, Placement.Edit)}
            create={AccessCheck.Create(permissions, Placement.RequiredRole, Placement.Create)}
            options={{ group: 'Inventory', leftIcon: <StorageIcon /> }} />,
          <Resource name="campaign_ad_qualities"
            list={AccessCheck.Read(permissions, CampaignAdQuality.RequiredRole, CampaignAdQuality.List)}
            show={AccessCheck.Read(permissions, CampaignAdQuality.RequiredRole, CampaignAdQuality.Show)}
            edit={AccessCheck.Read(permissions, CampaignAdQuality.RequiredRole, CampaignAdQuality.Edit)}
            create={AccessCheck.Create(permissions, CampaignAdQuality.RequiredRole, CampaignAdQuality.Create)}
            options={{ group: 'Campaign', label: 'Ad Quality Rules', leftIcon: <RadioButtonIcon /> }} />,
          <Resource name="campaign_sites"
            list={AccessCheck.Read(permissions, CampaignSite.RequiredRole, CampaignSite.List)}
            show={AccessCheck.Read(permissions, CampaignSite.RequiredRole, CampaignSite.Show)}
            edit={AccessCheck.Read(permissions, CampaignSite.RequiredRole, CampaignSite.Edit)}
            create={AccessCheck.Create(permissions, CampaignSite.RequiredRole, CampaignSite.Create)}
            options={{ group: 'Campaign', label: 'Site Campaigns', leftIcon: <RadioButtonIcon /> }} />,
          <Resource name="campaign_pmps"
            list={AccessCheck.Read(permissions, CampaignPmp.RequiredRole, CampaignPmp.List)}
            show={AccessCheck.Read(permissions, CampaignPmp.RequiredRole, CampaignPmp.Show)}
            edit={AccessCheck.Read(permissions, CampaignPmp.RequiredRole, CampaignPmp.Edit)}
            create={AccessCheck.Create(permissions, CampaignPmp.RequiredRole, CampaignPmp.Create)}
            options={{ group: 'Campaign', label: 'PMP Deals Management', leftIcon: <RadioButtonIcon /> }} />,
          <Resource name="campaign_rtbs"
            list={AccessCheck.Read(permissions, CampaignRtb.RequiredRole, CampaignRtb.List)}
            show={AccessCheck.Read(permissions, CampaignRtb.RequiredRole, CampaignRtb.Show)}
            edit={AccessCheck.Read(permissions, CampaignRtb.RequiredRole, CampaignRtb.Edit)}
            create={AccessCheck.Create(permissions, CampaignRtb.RequiredRole, CampaignRtb.Create)}
            options={{ group: 'Campaign', label: 'RTB Management', leftIcon: <RadioButtonIcon /> }} />,
          <Resource name="campaign_ad_tags"
            list={AccessCheck.Read(permissions, CampaignAdTag.RequiredRole, CampaignAdTag.List)}
            show={AccessCheck.Read(permissions, CampaignAdTag.RequiredRole, CampaignAdTag.Show)}
            edit={AccessCheck.Read(permissions, CampaignAdTag.RequiredRole, CampaignAdTag.Edit)}
            create={AccessCheck.Create(permissions, CampaignAdTag.RequiredRole, CampaignAdTag.Create)}
            options={{ group: 'Campaign', label: 'Ad Tag Management', leftIcon: <RadioButtonIcon /> }} />,
          <Resource name="macro_maps"
            list={AccessCheck.Read(permissions, MacroMap.RequiredRole, MacroMap.List)}
            show={AccessCheck.Read(permissions, MacroMap.RequiredRole, MacroMap.Show)}
            edit={AccessCheck.Read(permissions, MacroMap.RequiredRole, MacroMap.Edit)}
            create={AccessCheck.Create(permissions, MacroMap.RequiredRole, MacroMap.Create)}
            options={{ group: 'Configuration', label: 'Macro Maps', leftIcon: <CreateIcon /> }} />,
          <Resource name="ad_sizes"
            list={AccessCheck.Read(permissions, AdSize.RequiredRole, AdSize.List)}
            show={AccessCheck.Read(permissions, AdSize.RequiredRole, AdSize.Show)}
            edit={AccessCheck.Read(permissions, AdSize.RequiredRole, AdSize.Edit)}
            create={AccessCheck.Create(permissions, AdSize.RequiredRole, AdSize.Create)}
            options={{ group: 'Configuration', label: 'Ad Sizes', leftIcon: <CreateIcon /> }} />,
          <Resource name="js_functions"
            list={AccessCheck.Read(permissions, JsFunction.RequiredRole, JsFunction.List)}
            show={AccessCheck.Read(permissions, JsFunction.RequiredRole, JsFunction.Show)}
            edit={AccessCheck.Read(permissions, JsFunction.RequiredRole, JsFunction.Edit)}
            create={AccessCheck.Create(permissions, JsFunction.RequiredRole, JsFunction.Create)}
            options={{ group: 'Configuration', label: 'JS Functions', leftIcon: <CreateIcon /> }} />,
          <Resource name="creative_templates"
            list={AccessCheck.Read(permissions, CreativeTemplate.RequiredRole, CreativeTemplate.List)}
            show={AccessCheck.Read(permissions, CreativeTemplate.RequiredRole, CreativeTemplate.Show)}
            edit={AccessCheck.Read(permissions, CreativeTemplate.RequiredRole, CreativeTemplate.Edit)}
            create={AccessCheck.Create(permissions, CreativeTemplate.RequiredRole, CreativeTemplate.Create)}
            options={{ group: 'Configuration', label: 'Creative Templates', leftIcon: <CreateIcon /> }} />,
          <Resource name="server_files"
            list={AccessCheck.Read(permissions, ServerFile.RequiredRole, ServerFile.List)}
            show={AccessCheck.Read(permissions, ServerFile.RequiredRole, ServerFile.Show)}
            edit={AccessCheck.Read(permissions, ServerFile.RequiredRole, ServerFile.Edit)}
            create={AccessCheck.Create(permissions, ServerFile.RequiredRole, ServerFile.Create)}
            options={{ group: 'Configuration', label: 'Server Files', leftIcon: <CreateIcon /> }} />,
          <Resource name="property_groups"
            list={AccessCheck.Read(permissions, PropertyGroup.RequiredRole, PropertyGroup.List)}
            show={AccessCheck.Read(permissions, PropertyGroup.RequiredRole, PropertyGroup.Show)}
            edit={AccessCheck.Read(permissions, PropertyGroup.RequiredRole, PropertyGroup.Edit)}
            create={AccessCheck.Create(permissions, PropertyGroup.RequiredRole, PropertyGroup.Create)}
            options={{ group: 'Configuration', label: 'Property Groups', leftIcon: <CreateIcon /> }} />,
          <Resource name="properties"
            list={AccessCheck.Read(permissions, Property.RequiredRole, Property.List)}
            show={AccessCheck.Read(permissions, Property.RequiredRole, Property.Show)}
            edit={AccessCheck.Read(permissions, Property.RequiredRole, Property.Edit)}
            create={AccessCheck.Create(permissions, Property.RequiredRole, Property.Create)}
            options={{ group: 'Configuration', label: 'Properties', leftIcon: <CreateIcon /> }} />,
          <Resource name="macro_map_keys"
            list={AccessCheck.Read(permissions, MacroMapKey.RequiredRole, MacroMapKey.List)}
            show={AccessCheck.Read(permissions, MacroMapKey.RequiredRole, MacroMapKey.Show)}
            edit={AccessCheck.Read(permissions, MacroMapKey.RequiredRole, MacroMapKey.Edit)}
            create={AccessCheck.Create(permissions, MacroMapKey.RequiredRole, MacroMapKey.Create)}
            options={{ group: 'Configuration', label: 'Macro Map Keys', leftIcon: <CreateIcon /> }} />,
          <Resource name="monitors"
            list={AccessCheck.Read(permissions, Monitor.RequiredRole, Monitor.List)}
            show={AccessCheck.Read(permissions, Monitor.RequiredRole, Monitor.Show)}
            edit={AccessCheck.Read(permissions, Monitor.RequiredRole, Monitor.Edit)}
            create={AccessCheck.Create(permissions, Monitor.RequiredRole, Monitor.Create)}
            options={{ group: 'Report', label: 'Monitors', leftIcon: <BarChartIcon /> }} />,
          <Resource name="reports/linked_placements"
            list={AccessCheck.Read(permissions, ReportLinkedPlacement.RequiredRole, ReportLinkedPlacement.List)}
            options={{ group: 'Report', label: 'Linked Placements', leftIcon: <BarChartIcon /> }} />,
          <Resource name="reports/placements"
            list={AccessCheck.Read(permissions, ReportPlacement.RequiredRole, ReportPlacement.List)}
            options={{ group: 'Report', label: 'Placements', leftIcon: <BarChartIcon /> }} />,
          <Resource name="reports/sites"
            list={AccessCheck.Read(permissions, ReportSite.RequiredRole, ReportSite.List)}
            options={{ group: 'Report', label: 'Sites', leftIcon: <BarChartIcon /> }} />,
          <Resource name="reports/change_histories"
            list={AccessCheck.Read(permissions, ReportChangeHistory.RequiredRole, ReportChangeHistory.List)}
            options={{ group: 'Report', label: 'Change History', leftIcon: <BarChartIcon /> }} />,
          <Resource name="users"
            list={AccessCheck.Read(permissions, User.RequiredRole, User.List)}
            show={AccessCheck.Read(permissions, User.RequiredRole, User.Show)}
            edit={AccessCheck.Read(permissions, User.RequiredRole, User.Edit)}
            create={AccessCheck.Create(permissions, User.RequiredRole, User.Create)}
            options={{ group: 'Admin', leftIcon: <PersonIcon /> }} />,
          <Resource name="teams"
            list={AccessCheck.Read(permissions, Team.RequiredRole, Team.List)}
            show={AccessCheck.Read(permissions, Team.RequiredRole, Team.Show)}
            edit={AccessCheck.Read(permissions, Team.RequiredRole, Team.Edit)}
            create={AccessCheck.Create(permissions, Team.RequiredRole, Team.Create)}
            options={{ group: 'Admin', leftIcon: <PeopleIcon /> }} />,
          <Resource name="documentations"
            list={AccessCheck.Read(permissions, Documentation.RequiredRole, Documentation.List)}
            show={AccessCheck.Read(permissions, Documentation.RequiredRole, Documentation.Show)}
            edit={AccessCheck.Read(permissions, Documentation.RequiredRole, Documentation.Edit)}
            create={AccessCheck.Create(permissions, Documentation.RequiredRole, Documentation.Create)}
            options={{ group: 'Documentation', label: 'Documentation', leftIcon: <NoteIcon /> }} />,
          <Resource name="profiles" edit={Profile.Edit} />,
          <Resource name="helpers/ad_property_references" />,
          <Resource name="helpers/ad_sizes" />,
          <Resource name="helpers/browsers" />,
          <Resource name="helpers/campaigns/rtbs" />,
          <Resource name="helpers/campaigns/sites" />,
          <Resource name="helpers/change_histories" />,
          <Resource name="helpers/change_history_collections" />,
          <Resource name="helpers/content_categories" />,
          <Resource name="helpers/countries" />,
          <Resource name="helpers/creative_attributes" />,
          <Resource name="helpers/creative_templates" />,
          <Resource name="helpers/device_types" />,
          <Resource name="helpers/domains" />,
          <Resource name="helpers/js_functions" />,
          <Resource name="helpers/linked_campaigns" />,
          <Resource name="helpers/lookup_property_groups" />,
          <Resource name="helpers/lookup_property_group_references" />,
          <Resource name="helpers/lookup_properties" />,
          <Resource name="helpers/lookup_property_options" />,
          <Resource name="helpers/lookup_property_references" />,
          <Resource name="helpers/macro_map_keys" />,
          <Resource name="helpers/macro_map_references" />,
          <Resource name="helpers/networks" />,
          <Resource name="helpers/operating_systems" />,
          <Resource name="helpers/placement_codes" />,
          <Resource name="helpers/placement_ids" />,
          <Resource name="helpers/publisher_codes" />,
          <Resource name="helpers/publisher_ids" />,
          <Resource name="helpers/rtb_protocols" />,
          <Resource name="helpers/roles" />,
          <Resource name="helpers/sites" />,
          <Resource name="helpers/teams" />,
          <Resource name="helpers/users" />,
          <Resource name="helpers/user_ids" />
        ]}
      </Admin>
    </div>
  )
};

export default App;



// WEBPACK FOOTER //
// ./src/views/Framework/App.tsx